import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { apiService } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Receipt,
  FolderOpen,
  Tag,
  Clock,
  CheckCircle2,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Loader2,
} from "lucide-react";

const StatCard = ({ title, value, icon: Icon, color, trend }) => (
  <Card className="card-hover border-0 shadow-sm">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
          {trend && (
            <div className="flex items-center gap-1 mt-2">
              {trend > 0 ? (
                <TrendingUp className="w-4 h-4 text-emerald-500" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-500" />
              )}
              <span
                className={`text-xs font-medium ${trend > 0 ? "text-emerald-600" : "text-red-600"}`}
              >
                {Math.abs(trend)}% from last week
              </span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const TransactionRow = ({ transaction, statuses, categories }) => {
  const status = statuses.find((s) => s.id === transaction.status_id);
  const category = categories.find((c) => c.id === transaction.category_id);

  const formatAmount = (amount, direction) => {
    const formatted = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
    return direction === "income" ? `+${formatted}` : `-${formatted}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  return (
    <Link
      to={`/transactions/${transaction.id}`}
      className="flex items-center justify-between p-4 rounded-xl hover:bg-slate-50 transition-colors group"
      data-testid={`task-item-${transaction.id}`}
    >
      <div className="flex items-center gap-4 min-w-0">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            transaction.direction === "income"
              ? "bg-emerald-100"
              : "bg-red-100"
          }`}
        >
          {transaction.direction === "income" ? (
            <TrendingUp className="w-5 h-5 text-emerald-600" />
          ) : (
            <TrendingDown className="w-5 h-5 text-red-500" />
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900 truncate">
            {transaction.counterparty_name ||
              transaction.description?.substring(0, 40) ||
              "Transaction"}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-slate-500">
              {formatDate(transaction.transaction_date)}
            </span>
            <span className="text-xs text-slate-300">•</span>
            <span className="text-xs text-slate-500">{transaction.bank}</span>
            {category && (
              <>
                <span className="text-xs text-slate-300">•</span>
                <span className="text-xs text-slate-500">{category.name}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4">
        {status && (
          <Badge
            variant="secondary"
            style={{ backgroundColor: `${status.color}20`, color: status.color }}
            className="font-medium"
          >
            {status.name}
          </Badge>
        )}
        <span
          className={`text-sm font-semibold ${
            transaction.direction === "income"
              ? "text-emerald-600"
              : "text-red-500"
          }`}
        >
          {formatAmount(transaction.amount, transaction.direction)}{" "}
          {transaction.currency}
        </span>
        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
      </div>
    </Link>
  );
};

const MyDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tasksRes, statusesRes, categoriesRes] = await Promise.all([
          apiService.getMyTasks(),
          apiService.getStatuses(),
          apiService.getCategories(),
        ]);
        setTasks(tasksRes.data);
        setStatuses(statusesRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="my-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          Welcome back, {user?.name?.split(" ")[0]}
        </h1>
        <p className="text-slate-500 mt-1">
          Here&apos;s an overview of your assigned tasks
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Tasks"
          value={tasks?.total_tasks || 0}
          icon={Receipt}
          color="bg-emerald-500"
        />
        <StatCard
          title="Needs Receipt"
          value={tasks?.needs_receipt || 0}
          icon={FolderOpen}
          color="bg-amber-500"
        />
        <StatCard
          title="Needs Category"
          value={tasks?.needs_category || 0}
          icon={Tag}
          color="bg-violet-500"
        />
        <StatCard
          title="Overdue"
          value={tasks?.overdue || 0}
          icon={AlertTriangle}
          color="bg-red-500"
        />
      </div>

      {/* Recent Tasks */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold">My Tasks</CardTitle>
            <Link to="/transactions">
              <Button variant="ghost" size="sm" data-testid="view-all-tasks">
                View All
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {tasks?.transactions?.length > 0 ? (
            <ScrollArea className="h-[400px]">
              <div className="space-y-1">
                {tasks.transactions.map((transaction) => (
                  <TransactionRow
                    key={transaction.id}
                    transaction={transaction}
                    statuses={statuses}
                    categories={categories}
                  />
                ))}
              </div>
            </ScrollArea>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">
                All caught up!
              </h3>
              <p className="text-slate-500 mt-1 max-w-sm">
                You have no pending tasks. Check back later or ask your manager
                to assign new transactions.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Link to="/transactions" className="group">
          <Card className="border-0 shadow-sm card-hover h-full">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Receipt className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">
                    Browse Transactions
                  </h3>
                  <p className="text-sm text-slate-500">
                    View and filter all transactions
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/inbox" className="group">
          <Card className="border-0 shadow-sm card-hover h-full">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <FolderOpen className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">
                    Upload Receipt
                  </h3>
                  <p className="text-sm text-slate-500">
                    Add receipts to inbox
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        {(user?.role === "admin" || user?.role === "manager") && (
          <Link to="/import" className="group">
            <Card className="border-0 shadow-sm card-hover h-full">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      Import Statements
                    </h3>
                    <p className="text-sm text-slate-500">
                      Upload MT940 bank files
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        )}
      </div>
    </div>
  );
};

export default MyDashboard;
